/* eslint-disable no-console, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-enum-comparison, @typescript-eslint/no-unsafe-return */
import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT, isPlatformServer } from '@angular/common';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  inject,
  Inject,
  Input,
  NgZone,
  PendingTasks,
  PLATFORM_ID,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { PageDataResponse } from '@core-mkt/resolvers/page-data/page-data-response';
import { CountdownService } from '@core-mkt/services/countdown/countdown.service';
import { CouponAggregationService } from '@core-mkt/services/coupon-aggregation/coupon-aggregation.service';
import { CouponQueryResolverService } from '@core-mkt/services/coupon-query-resolver/coupon-query-resolver.service';
import { COUPON_SCROLL_TARGET, CouponScrollService } from '@core-mkt/services/coupon-scroll/coupon-scroll.service';
import { CouponUseCases } from '@core-mkt/services/coupon-query-resolver/coupon-use-cases';
import { ImgixService } from '@core-mkt/services/imgix/imgix.service';
import { LazyImgDirective } from '@core-mkt/services/lazy-load/lazy-load.directive';
import { PricingService } from '@core-mkt/services/pricing/pricing.service';
import { ProductService } from '@core-mkt/services/product/product.service';
import { SchemaService } from '@core-mkt/services/schema/schema.service';
import type { ComponentTheme, ThemeInterface } from '@core-mkt/services/theme-parser/theme-parser.service';
import { ThemeParserService } from '@core-mkt/services/theme-parser/theme-parser.service';
import { TrustpilotService } from '@core-mkt/services/trustpilot/trustpilot.service';
import { ServerWistiaService } from '@core-mkt/services/wistia/server-wistia.service';
import { WistiaService } from '@core-mkt/services/wistia/wistia.service';
import { WysiwygRedactorService } from '@core-mkt/services/wysiwg-redactor/wysiwyg-redactor.service';
import type { CraftXGritProductEntry } from '@core-mkt/interfaces/craft-xgrit-product-entry';
import type { XGritCompleteProduct } from '@core-mkt/services/xgrit-api/xgrit-product';
import type { VisionButton } from '@core-mkt/shared/components/vision-button/vision-button/vision-button';
import type { VisionVideoButton } from '@core-mkt/shared/components/vision-video-button/vision-video-button/vision-video-button';
import { logError, SEVERITY } from '@core-mkt/shared/lib/logger';
import { CheckoutUrlBuilderService } from '@core-mkt/shared/services/checkout-url-builder/checkout-url-builder.service';
import type { Observable } from 'rxjs';
import { EMPTY, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import type {
  BottomRightContent,
  CategoryWidgets,
  FeaturedContent,
  newSecondButtonData,
  VideoButton,
} from './top-section';
import { TopSection } from './top-section';

import { faAngleDown, faAngleUp, faTimes } from '@fortawesome/free-solid-svg-icons';

import { Brand } from '@core-mkt/enums/brand.enum';
import { AccessibilityService } from '@core-mkt/services/accessibility/accessibility.service';
import { EnvService } from '@core-mkt/services/env/env.service';
import { ReiReferralService } from '@core-mkt/services/rei-referral/rei-referral.service';
import { XGritApiService } from '@core-mkt/services/xgrit-api/xgrit-api.service';
import { GlobalCouponOverrideService } from '@core-mkt/shared/services/global-coupon-override/global-coupon-override.service';
import { VisionModalService } from '../../vision-modal/vision-modal.service';
import type { WebinarScheduleContent, WebinarScheduleResponse } from '../../webinar-schedule/webinar-schedule';

import { CommonModule } from '@angular/common';
import { DurationTextPipe } from '@core-mkt/services/duration/duration-text.pipe';
import { ReferralBannerComponent } from '@core-mkt/shared/components/referral-banner/referral-banner.component';
import { TopSectionButtonGroupComponent } from './button-group/top-section-button-group.component';
import { TopSectionCarouselComponent } from './carousel/top-section-carousel.component';
import { TopSectionCoursePopupBannerComponent } from './course-popup-banner/top-section-course-popup-banner.component';
import { FeaturedContentComponent } from './featured-content/featured-content.component';
import { getBrandSiteUrl } from '@core-mkt/services/schema/organization.schema';
import { getTopSectionTrustpilotSchema } from './top-section.schema';
import { TopSectionTrustpilotComponent } from './trustpilot/top-section-trustpilot.component';
import { TopSectionWysiwygComponent } from './wysiwyg/top-section-wysiwyg.component';

/** Upper bound on the SSR Trustpilot fetch so a stalled call can't hang the render. */
const TRUSTPILOT_SSR_TIMEOUT_MS = 5000;

/** Length of the email-capture price roll-down, matching Pricing Table. */
const PRICE_ROLL_DURATION_MS = 2000;

@Component({
  selector: 'top-section',
  templateUrl: './top-section.component.html',
  styleUrls: ['./top-section.scss'],
  standalone: true,
  imports: [
    CommonModule,
    DurationTextPipe,
    TopSectionButtonGroupComponent,
    TopSectionCarouselComponent,
    TopSectionCoursePopupBannerComponent,
    FeaturedContentComponent,
    TopSectionTrustpilotComponent,
    TopSectionWysiwygComponent,
    LazyImgDirective,
    ReferralBannerComponent,
  ],
})
export class TopSectionComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() data: TopSection;
  @ViewChild('prices', { static: false }) priceWrapperEl: ElementRef;
  faTimes = faTimes;
  faAngleDown = faAngleDown;
  faAngleUp = faAngleUp;
  hasContent = false;
  modalHasContent = false;
  showTitle = false;
  showContent = false;
  showModal = false;
  product: XGritCompleteProduct;
  theme: ThemeInterface;
  originalPrice: number;
  discountPrice: number;
  countdown: Observable<number>;
  offerEndTime: number = null;
  daysRemaining = false;
  productCtaLink: string;
  productCtaLinkAriaLabel: string;
  pricingText: string;
  percentSaved: number;
  percentSavedText: string;
  wistiaEmbed: string = null;
  modalContent: string;
  wistiaCtaData: VideoButton;
  componentClasses = '';
  private readonly brandId: string;
  showMobileBorder: boolean;
  newsecondButtonData: newSecondButtonData;
  feeModalContent: string;
  showScheduleCTA = false;
  ctaDisplay = true;
  private destroy$ = new Subject<void>();
  private readonly couponScroll = inject(CouponScrollService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly zone = inject(NgZone);
  private readonly breakpointObserver = inject(BreakpointObserver);
  /**
   * Bumped per product fetch and on destroy, so a slower, older response, or one landing after
   * the view is gone, can't overwrite the current price.
   */
  private productFetchSeq = 0;
  /** Shows the "Coupon Applied!" pill; set when the email-capture roll-down starts (EPS-4801). */
  couponApplied = false;
  /** Held from the scroll-back settling until the roll ends, so a coupon broadcast can't re-render mid-roll. */
  private captureAnimationClaimed = false;
  /** The capture price, requested while the scroll-back is still moving so the roll can start as it settles. */
  private capturePrice: Promise<XGritCompleteProduct> | null = null;
  /** Marks the price row as an email-capture scroll-back target. */
  readonly couponScrollTarget = COUPON_SCROLL_TARGET.topSection;
  private priceRollTimer: ReturnType<typeof setInterval> | null = null;
  activeCoupon = '';
  globalSalesQueryParam: string;
  globalCDIQueryParam: string;
  showReiEyebrow = false;
  childFieldsToGetPricing: string[] = [];
  private originalContentData: {
    rightContent: string;
    eyebrow: string;
    sectionTitle: any;
    sectionContent: any;
    modalContent: any;
    highlightedTextModal1?: any[];
    highlightedTextModal2?: any[];
    featuredContent?: any[];
    bottomRightContent?: any[];
  };

  // Carousel data
  hasCarouselData = false;

  constructor(
    private redactor: WysiwygRedactorService,
    private themeParser: ThemeParserService,
    private ps: ProductService,
    private countdownService: CountdownService,
    public imgixService: ImgixService,
    private pricing: PricingService,
    private couponAggregationService: CouponAggregationService,
    private checkoutUrlBuilderService: CheckoutUrlBuilderService,
    private wistia: ServerWistiaService,
    private clientWistia: WistiaService,
    private env: EnvService,
    private modal: VisionModalService,
    private xas: XGritApiService,
    private accessibilityService: AccessibilityService,
    private globalCouponOverrideService: GlobalCouponOverrideService,
    private couponQueryResolverService: CouponQueryResolverService,
    private route: ActivatedRoute,
    private reiReferralService: ReiReferralService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
    private schemaService: SchemaService,
    private trustpilotService: TrustpilotService,
    @Inject(PLATFORM_ID) private platformId: object,
    private pendingTasks: PendingTasks,
  ) {
    this.globalSalesQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.SALES);
    this.globalCDIQueryParam = this.couponQueryResolverService.setCouponQueryParam(CouponUseCases.CDI);

    const envConfig = this.env.get;
    this.brandId = envConfig.xgritApiConfig.baseParams.brandId;
    this.showMobileBorder = (envConfig.brandConfig.designMode ?? 'ace') === 'ace';
  }

  /**
   * Returns the label for the money guarantee label based on the brand ID.
   * @returns The label for the money guarantee.
   */
  get moneyGuaranteeLabel(): string {
    return this.brandId === Brand.AARP ? 'Money-Back Guarantee!**' : '100% Money Back Guarantee';
  }

  get isLink(): boolean {
    return this.data.ctaLink !== null && !this.data.ctaLink.includes('#');
  }

  get imgAlign(): string {
    return this.data.contentAlignment ? 'lg:' + this.data.contentAlignment : 'lg:flex-row mx-auto';
  }

  get courseOverviewPopup(): boolean {
    return this.data?.courseOverviewPopup;
  }

  /**
   * returns cta button data.
   * @returns cta button data.
   */
  get ctaButtonData(): VisionButton {
    return {
      buttonStyle: this.data.ctaType,
      bgTheme: this.componentClasses,
      buttonText: this.ctaLabel,
      buttonLink: this.ctaLink,
      buttonAriaLabel: this.ctaLinkAriaLabel,
      fullWidth: false,
      nofollow: this.ctaNofollow,
      noreferrer: this.ctaNewTab,
      noopener: this.ctaNewTab,
      newTab: this.ctaNewTab,
      ctaTag: this.ctaTag,
    };
  }

  get wistiaButtonData(): VisionVideoButton {
    if (this.hasSecondButtonData && this.videoButtonType != 'button') {
      this.wistiaCtaData = this.data.videoButton[0];
      return {
        bgTheme: this.componentClasses,
        ctaType: this.wistiaCtaData?.ctaType,
        ctaLabel: this.wistiaCtaData?.ctaLabel || 'Watch Overview',
        wistiaId: this.wistiaCtaData?.wistiaId || '',
      };
    }
  }

  get secondButtonData(): VisionButton {
    if (this.hasSecondButtonData && this.videoButtonType === 'button') {
      this.newsecondButtonData = this.data.videoButton[0];
      return {
        bgTheme: this.componentClasses,
        buttonStyle: this.newsecondButtonData?.ctaType,
        buttonText: this.newsecondButtonData?.ctaLabel || 'Start Today',
        buttonLink: this.newsecondButtonData?.ctaOtherlink,
        fullWidth: false,
        nofollow: this.ctaNofollow,
        noreferrer: this.ctaNewTab,
        noopener: this.ctaNewTab,
        newTab: this.ctaNewTab,
        ctaTag: this.ctaTag,
      };
    }
  }

  get ctaType(): string {
    return this.data.ctaType === null
      ? 'bg-ace-blue-600 hover:bg-ace-blue-500 text-vis-reverse h-12 inline-flex items-center justify-center'
      : this.data.ctaType;
  }

  get viewScheduleLabel(): string {
    return 'View Schedule';
  }

  get showPriceCta(): boolean {
    return this.data.showPricing && this.product !== undefined;
  }

  get showPricing(): boolean {
    return this.data.showPricing;
  }

  get showScroll(): boolean {
    return this.data.ctaArrowDirection !== null && !this.isLink;
  }

  get reiEyebrowLabel(): string {
    return this.reiReferralService.eyebrowLabel;
  }

  get reiLogoSrc(): string {
    const isDarkBackground = this.theme?.root?.includes('bg-vis-dark');
    return isDarkBackground ? this.reiReferralService.logoSrcDark : this.reiReferralService.logoSrcLight;
  }

  get displayOfferTimer(): boolean {
    return this.data.showOfferTimer;
  }

  get ctaLabel(): string {
    return this.data.ctaLabel === null ? 'Start Today' : this.data.ctaLabel;
  }

  get ctaLink(): string {
    if (this.isLink) {
      return this.data.ctaLink;
    }
    if (
      this.data.ctaLink &&
      this.data.ctaLink !== null &&
      this.data.ctaLink.includes('#') &&
      this.data.ctaLink.includes('http://')
    ) {
      const strippedLink = this.data.ctaLink.replace('http://', '');
      return strippedLink;
    }
    return this.data.ctaLink && this.data.ctaLink !== null ? this.data.ctaLink : this.productCtaLink;
  }

  get ctaLinkAriaLabel(): string {
    return this.data.ctaLink && this.data.ctaLink !== null ? '' : this.productCtaLinkAriaLabel;
  }

  get ctaTag(): string {
    return this.data.ctaTag !== null ? this.data.ctaTag : '';
  }

  get ctaNewTab(): boolean {
    return this.data.ctaNewTab;
  }

  get checkStyle(): string {
    return this.env.get.brandConfig.checkClassName;
  }

  get ctaNofollow(): boolean {
    return this.data.ctaNofollow;
  }

  get buttonScrollArrowDirection(): string {
    return this.data.ctaArrowDirection;
  }

  get hasSecondButtonData(): boolean {
    return this.data.videoButton && this.data.videoButton.length > 0;
  }

  get categoryWidgets(): CategoryWidgets[] {
    if (this.data['categoryWidgets']?.length > 0) {
      return this.data['categoryWidgets'];
    }
  }

  get featuredContent(): FeaturedContent {
    if (
      this.data.featuredContent &&
      this.data.featuredContent.length > 0 &&
      this.data.featuredContent[0].featuredContentText !== null &&
      this.data.featuredContent[0].featuredContentIcon.length > 0
    ) {
      this.data.featuredContent[0]['backgroundColor'] =
        this.componentClasses.includes('bg-vis-dark') || this.componentClasses.includes('bg-ace-grey-900')
          ? 'light'
          : 'dark';
      return this.data.featuredContent[0];
    }
  }

  get bottomRightContent(): BottomRightContent[] {
    return this.data.bottomRightContent !== null && this.data.bottomRightContent.length > 0
      ? this.data.bottomRightContent
      : null;
  }

  get videoButtonType(): string {
    return this.data.videoButton[0].buttonType || this.data.videoButton[0].buttontype;
  }

  get topSectionContentSplit(): string {
    return this.data.topSectionContentSplit;
  }

  get showCarousel(): boolean {
    return this.hasCarouselData;
  }

  private get hasRightContent(): boolean {
    return !!(this.data.rightContent || this.categoryWidgets || this.showCarousel);
  }

  ngOnInit(): void {
    // Check if carousel data exists with at least one valid image
    // Craft CMS sets a minimum of 1 entry, so we must check for actual image content
    if (this.data.topSectionCarousel?.some((slide) => slide.topSectionCarouselImage?.[0] != null)) {
      this.hasCarouselData = true;
    }

    // Refine border: only show when right content exists (stacks below on mobile)
    this.showMobileBorder = this.showMobileBorder && this.hasRightContent;

    // Store original content before any processing
    this.storeOriginalContent();

    this.componentClasses = this.data.styles ? this.data.styles.join(' ') : '';
    if (this.hasSecondButtonData && this.videoButtonType === 'videoButton') {
      this.wistia.insertWistiaCtaData(this.wistiaButtonData.wistiaId);
    }
    this.offerEndTime = this.getOfferEndTime();
    this.countdown = this.countdownService.create(this.offerEndTime);
    if (this.offerEndTime) {
      this.countdown.pipe(takeUntil(this.destroy$)).subscribe((data) => {
        if (data > 0) {
          const milli = data;
          const hours = milli / 1000 / 60 / 60;
          this.daysRemaining = hours > 24;
        } else {
          // Countdown expired - ensure daysRemaining is false
          this.daysRemaining = false;
        }
      });
    }
    if (this.data.eyebrow !== null) {
      this.hasContent = true;
      this.showTitle = true;
      this.data.eyebrow = this.redactor.processContentWithPlaceholderRemoval(this.data.eyebrow);
    } else if (this.reiReferralService.isReiReferred()) {
      this.showReiEyebrow = true;
      this.hasContent = true;
      this.showTitle = true;
    }
    if (this.data.sectionTitle !== null) {
      this.hasContent = true;
      this.showTitle = true;
      this.data.sectionTitle = this.redactor.processContentWithPlaceholderRemoval(this.data.sectionTitle);
    }
    if (this.data.modalContent !== null) {
      this.hasContent = true;
      this.modalHasContent = true;
      this.data.modalContent = this.redactor.processContentWithPlaceholderRemoval(this.data.modalContent);
    }
    if (this.data.highlightedTextModal1?.length > 0) {
      this.data.highlightedTextModal1.forEach((item: { modalContent?: string }) => {
        if (item.modalContent) {
          item.modalContent = this.redactor.processContentWithPlaceholderRemoval(item.modalContent);
        }
      });
    }
    if (this.data.highlightedTextModal2?.length > 0) {
      this.data.highlightedTextModal2.forEach((item: { modalContent?: string }) => {
        if (item.modalContent) {
          item.modalContent = this.redactor.processContentWithPlaceholderRemoval(item.modalContent);
        }
      });
    }

    const productEntry = Array.isArray(this.data?.product) ? this.data?.product[0] : this.data?.product;
    if (productEntry && productEntry.webinarCourseIds && this.hasValidCourseIds(productEntry.webinarCourseIds)) {
      const webinarTitle = productEntry.webinarTitle;
      const webinarContent = productEntry.webinarContent;
      // Pass the entire product entry to let the service determine the appropriate category
      this.xas
        .getWebinarSchedule(productEntry.webinarCourseIds, productEntry, webinarTitle, webinarContent)
        .pipe(
          takeUntil(this.destroy$),
          catchError((error) => {
            logError({
              error,
              message: 'WEBINAR_SCHEDULE_LOAD_FAILED',
              severity: SEVERITY.WARN,
              tags: ['top-section', 'webinar-schedule', 'http-error'],
              contextId: productEntry?.title || 'unknown-product-entry',
            });
            // Continue gracefully - webinar schedule is optional functionality
            return EMPTY;
          }),
        )
        .subscribe((webinarContent: WebinarScheduleResponse[]) => {
          // Enable Webinar Schedule CTA — template also checks hasWebinarData for rendering
          this.showScheduleCTA = true;
          this.data.webinarData = webinarContent;
        });
    }

    // Process product data immediately - CouponAggregationService reads banner coupon from route data
    this.processProductData();

    this.pricing.couponCodeEvent.pipe(takeUntil(this.destroy$)).subscribe((coupon: string | null) => {
      if (coupon && coupon.trim() !== '') {
        // Check if an email or phone capture coupon was just applied
        const couponCollection = this.couponAggregationService.collectAllCoupons();
        const captureCoupon = couponCollection?.coupons?.emailCapture || couponCollection?.coupons?.phoneCapture;

        if (captureCoupon?.trim()) {
          // Mid scroll-back (a fresh submission): request the new price now, and roll it once the
          // scroll settles (scrollSettled$ below). Otherwise, unless a roll is running, just take it.
          // A returning visitor's on-load price comes from processProductData(); this replay arrives
          // before the price row exists, so the priceWrapperEl guards make it a no-op.
          if (this.couponScroll.scrollPending()) {
            this.prefetchCapturePrice();
          } else if (!this.captureAnimationClaimed) {
            this.refreshCapturePrice();
          }
        } else {
          // Standard coupon event handling
          this.activeCoupon = `&${this.globalCDIQueryParam}=${coupon}`;
          if (this.productCtaLink) {
            this.productCtaLink = this.getCouponCodeToLink(this.productCtaLink);
          }
        }
      }
    });

    // EPS-4801: every priced Top Section rolls down, with the pill, once the scroll-back settles.
    this.couponScroll.scrollSettled$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.animateEmailCapturePrice();
    });

    if (!this.hasContent) {
      this.hasContent =
        this.data.productSeal.length > 0 || this.data.ctaLabel?.length > 0 || this.data.ctaLink?.length > 0;
    }
    const themeInput: ComponentTheme = {
      styles: this.data.styles,
      backgroundColor: this.componentClasses,
      backgroundImage: this.data.backgroundImage?.[0],
      textColor: this.data.textColor,
      columnCount: 0,
      columnDistribution: '',
    };
    this.theme = this.themeParser.getThemeClasses(themeInput);
  }

  async ngAfterViewInit(): Promise<void> {
    // Wait for nav links to be loaded before processing anchor tags
    await this.accessibilityService.waitForNavLinks();

    if (this.data.sectionContent !== null) {
      this.hasContent = true;
      this.showContent = true;
      const src = this.redactor.extractFirstImageSrc(this.data.sectionContent);
      this.data.sectionContent = this.redactor.processContentWithPlaceholderRemoval(this.data.sectionContent, 'lcp');
      // Only inject preload links during SSR to avoid duplicate <link rel="preload"> tags on the client
      if (src && typeof window === 'undefined') {
        this.injectPreloadLink(src);
      }
    }

    this.registerSchemaNodes();
  }

  /**
   * Registers Schema.org structured data nodes for this page.
   * EducationalOrganization with AggregateRating is registered only when
   * Trustpilot stars are enabled and data is available.
   */
  private registerSchemaNodes(): void {
    // SSR-only. SchemaService is a no-op in the browser, so the Trustpilot fetch
    // and PendingTasks registration would be pure waste on the client and could
    // delay ApplicationRef.isStable / hydration. Skip entirely off the server.
    if (!isPlatformServer(this.platformId)) {
      return;
    }

    if (this.data.showTrustpilotStars && this.env.get.brandConfig.trustpilotId?.length > 0) {
      // Canonical brand site root so this org @id matches every other emitter
      // (Course.provider, fallback, etc.) and dedups to one node. See EPS-3951.
      const siteUrl = getBrandSiteUrl(this.env.get.brandConfig.url);
      const organizationName = this.env.get.brandConfig.name || '';

      // Register a pending task so Angular's isStable waits for the Trustpilot API
      // response before finalizing the JSON-LD schema. Without this, the axios-based
      // fetch (which zone.js does not track in SSR) races against finalize().
      const done = this.pendingTasks.add();

      // getBrandStarsRating() bounds its upstream call with a 3s axios timeout. This race is
      // defense-in-depth: if that contract regresses, SSR would hang and SchemaService.finalize()
      // would never run, so done() must always fire and the render can complete.
      let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<null>((resolve) => {
        timeoutHandle = setTimeout(() => resolve(null), TRUSTPILOT_SSR_TIMEOUT_MS);
      });

      Promise.race([this.trustpilotService.getBrandStarsRating(), timeout])
        .then((rating) => {
          if (!rating) {
            return; // timed out — omit org structured data gracefully
          }
          this.schemaService.register(
            getTopSectionTrustpilotSchema({
              siteUrl,
              organizationName,
              trustScore: rating.score.trustScore,
              reviewCount: rating.numberOfReviews.total,
            }),
          );
        })
        .catch(() => {
          // Trustpilot data unavailable — structured data for org is omitted gracefully
        })
        .finally(() => {
          if (timeoutHandle) {
            clearTimeout(timeoutHandle);
          }
          done();
        });
    }
  }

  handleCtaDisplayChange(ctaDisplayValue: boolean): void {
    this.ctaDisplay = ctaDisplayValue;
  }

  private processProductData(): void {
    if (!this.data.product) return;

    if (Array.isArray(this.data.product)) {
      this.data.product = this.data.product[0];
    }

    this.hasContent = true;

    this.loadProductDetails();
  }

  /**
   * Starts the email-capture roll-down after the scroll-back settles, pricing in the capture
   * coupon (already in its cookie) with the request made during the scroll, or a fresh one.
   */
  private animateEmailCapturePrice(): void {
    if (!this.data.product || !this.priceWrapperEl) return;

    this.captureAnimationClaimed = true;
    const request = this.capturePrice ?? this.ps.getProductData(this.productEntry);
    this.capturePrice = null;
    this.applyCapturePrice(request, (product) => this.animatePriceChange(product));
  }

  /** The linked product entry (Craft may deliver it wrapped in a one-item array). */
  private get productEntry(): CraftXGritProductEntry {
    return Array.isArray(this.data.product) ? this.data.product[0] : this.data.product;
  }

  private prefetchCapturePrice(): void {
    if (!this.data.product || !this.priceWrapperEl || this.capturePrice !== null) return;

    this.capturePrice = this.ps.getProductData(this.productEntry);
    // Handled when the scroll settles; keep an early failure from reporting as unhandled meanwhile.
    this.capturePrice.catch(() => undefined);
  }

  /** Applies a capture coupon's price with no animation. */
  private refreshCapturePrice(): void {
    if (!this.data.product || !this.priceWrapperEl) return;

    this.applyCapturePrice(this.ps.getProductData(this.productEntry), (product) => this.handleProductData(product));
  }

  private applyCapturePrice(
    request: Promise<XGritCompleteProduct>,
    apply: (product: XGritCompleteProduct) => void,
  ): void {
    const seq = ++this.productFetchSeq;
    request
      .then(
        (product) => {
          if (seq === this.productFetchSeq) apply(product);
        },
        (error: unknown) => {
          this.captureAnimationClaimed = false;
          logError({
            error: error instanceof Error ? error : new Error(String(error)),
            message: 'CAPTURE_PRICE_FETCH_FAILED',
            severity: SEVERITY.WARN,
            tags: ['top-section', 'email-capture'],
          });
          // Fall back to a fresh product load.
          if (seq === this.productFetchSeq) this.processProductData();
        },
      )
      .catch((error: unknown) => {
        // A bug in rendering or rolling the new price, not a failed fetch: surface it.
        this.captureAnimationClaimed = false;
        logError({
          error: error instanceof Error ? error : new Error(String(error)),
          message: 'CAPTURE_PRICE_UPDATE_FAILED',
          severity: SEVERITY.WARN,
          tags: ['top-section', 'email-capture'],
        });
      });
  }

  /**
   * Renders the discounted price and the "Coupon Applied!" pill in one pass, then rolls the
   * discounted price down from the price the visitor was already looking at.
   */
  private animatePriceChange(product: XGritCompleteProduct): void {
    // What the visitor saw before the coupon — a running sale's price, not the list price, so the
    // roll never jumps up first. Falls back to the list price if nothing had rendered yet.
    const start = this.discountPrice ?? product.apiData.originalPrice;
    const end = product.apiData.discountPrice;

    this.handleProductData(product);

    // The coupon didn't lower this product's price (not eligible, or a better discount already
    // won), so there is nothing to confirm: no pill, no roll. A $0 is the pricing-failure
    // fallback, never a real discount.
    if (!(end > 0 && end < start)) {
      this.captureAnimationClaimed = false;
      return;
    }

    this.couponApplied = true;

    // Reduced motion: the pill and the discounted price, without the roll (the pill's slide-in is off in CSS).
    if (this.breakpointObserver.isMatched('(prefers-reduced-motion: reduce)')) {
      this.captureAnimationClaimed = false;
      return;
    }

    // [innerHtml] replaces the price nodes when the new price renders, so render now and roll the
    // fresh nodes; the pill lands in the same pass.
    this.cdr.detectChanges();
    this.rollDownPrice(start, end);
  }

  private rollDownPrice(start: number, end: number): void {
    const wrapper = this.priceWrapperEl?.nativeElement as HTMLElement | undefined;
    const priceEl = wrapper?.querySelector<HTMLElement>('.disc-price');
    if (!priceEl) {
      this.captureAnimationClaimed = false;
      return;
    }

    // Put back on the last step, so the roll ends on exactly what rendered.
    const finalNodes = Array.from(priceEl.childNodes);
    // Whole-cent $1 steps, to avoid float drift, formatted the way getPricingText() renders prices
    // (`.00` dropped: `$49`, `$49.99`).
    const steps = Math.ceil(Math.round((start - end) * 100) / 100);
    const frame = (step: number): string =>
      '$' + (Math.round((start - step) * 100) / 100).toFixed(2).replace(/\.00$/, '');
    let step = 0;
    priceEl.textContent = frame(step);

    this.stopPriceRoll();
    // Each tick only rewrites this one node, so keep it from running app-wide change detection.
    this.zone.runOutsideAngular(() => {
      this.priceRollTimer = setInterval(() => {
        step += 1;
        // Done, or the price re-rendered under the roll: stop writing into it.
        if (step >= steps || !priceEl.isConnected) {
          if (priceEl.isConnected) priceEl.replaceChildren(...finalNodes);
          this.stopPriceRoll();
          this.captureAnimationClaimed = false;
          return;
        }
        priceEl.textContent = frame(step);
      }, PRICE_ROLL_DURATION_MS / steps);
    });
  }

  private stopPriceRoll(): void {
    if (this.priceRollTimer) {
      clearInterval(this.priceRollTimer);
      this.priceRollTimer = null;
    }
  }

  private loadProductDetails(): void {
    if (this.data.product === undefined) return;

    const seq = ++this.productFetchSeq;
    this.ps
      .getProductData(Array.isArray(this.data.product) ? this.data.product[0] : this.data.product)
      .then((product: XGritCompleteProduct) => {
        if (seq === this.productFetchSeq) this.handleProductData(product);
      })
      .catch((error) => console.error('Error fetching product data:', error));
  }

  private handleInlinePricing(): void {
    // Store original content for pricing updates
    const originalData = this.getOriginalContentData();

    // Update fields that had pricing placeholders in original content
    if (this.redactor.containsPricingPlaceholders(originalData.rightContent)) {
      this.data.rightContent = this.pricing.getPricingText(this.product, originalData.rightContent);
    }

    if (this.redactor.containsPricingPlaceholders(originalData.eyebrow)) {
      this.data.eyebrow = this.pricing.getPricingText(this.product, originalData.eyebrow);
    }

    if (this.redactor.containsPricingPlaceholders(originalData.sectionTitle)) {
      this.data.sectionTitle = this.pricing.getPricingText(this.product, originalData.sectionTitle);
    }

    if (this.redactor.containsPricingPlaceholders(originalData.sectionContent)) {
      this.data.sectionContent = this.pricing.getPricingText(this.product, originalData.sectionContent);
    }

    if (this.redactor.containsPricingPlaceholders(originalData.modalContent)) {
      this.data.modalContent = this.pricing.getPricingText(this.product, originalData.modalContent);
    }

    // Handle array fields with original content
    if (this.data.highlightedTextModal1?.length > 0 && originalData.highlightedTextModal1) {
      this.data.highlightedTextModal1.forEach((item, index) => {
        const originalItem = originalData.highlightedTextModal1[index];
        if (this.redactor.containsPricingPlaceholders(originalItem?.modalContent)) {
          item.modalContent = this.pricing.getPricingText(this.product, originalItem.modalContent);
        }
      });
    }

    if (this.data.highlightedTextModal2?.length > 0 && originalData.highlightedTextModal2) {
      this.data.highlightedTextModal2.forEach((item, index) => {
        const originalItem = originalData.highlightedTextModal2[index];
        if (this.redactor.containsPricingPlaceholders(originalItem?.modalContent)) {
          item.modalContent = this.pricing.getPricingText(this.product, originalItem.modalContent);
        }
      });
    }

    if (this.data.featuredContent?.length > 0 && originalData.featuredContent) {
      this.data.featuredContent.forEach((item, index) => {
        const originalItem = originalData.featuredContent[index];
        if (this.redactor.containsPricingPlaceholders(originalItem?.featuredContentText)) {
          item.featuredContentText = this.pricing.getPricingText(this.product, originalItem.featuredContentText);
        }
      });
    }

    if (this.data.bottomRightContent?.length > 0 && originalData.bottomRightContent) {
      this.data.bottomRightContent.forEach((item, index) => {
        const originalItem = originalData.bottomRightContent[index];
        if (this.redactor.containsPricingPlaceholders(originalItem?.sealIconText)) {
          item.sealIconText = this.pricing.getPricingText(this.product, originalItem.sealIconText);
        }
      });
    }
  }

  private storeOriginalContent(): void {
    // Store original content before any processing
    this.originalContentData = {
      rightContent: this.data.rightContent,
      eyebrow: this.data.eyebrow,
      sectionTitle: this.data.sectionTitle,
      sectionContent: this.data.sectionContent,
      modalContent: this.data.modalContent,
      highlightedTextModal1: this.data.highlightedTextModal1
        ? JSON.parse(JSON.stringify(this.data.highlightedTextModal1))
        : undefined,
      highlightedTextModal2: this.data.highlightedTextModal2
        ? JSON.parse(JSON.stringify(this.data.highlightedTextModal2))
        : undefined,
      featuredContent: this.data.featuredContent ? JSON.parse(JSON.stringify(this.data.featuredContent)) : undefined,
      bottomRightContent: this.data.bottomRightContent
        ? JSON.parse(JSON.stringify(this.data.bottomRightContent))
        : undefined,
    };
  }

  private getOriginalContentData() {
    return this.originalContentData;
  }

  private handleProductData(product: XGritCompleteProduct): void {
    this.product = product;
    this.ps.setProduct(product);
    this.handleInlinePricing();
    this.feeModalContent = product.apiData.feeDescription;
    this.originalPrice = product.apiData.originalPrice;
    this.discountPrice = product.apiData.discountPrice;
    this.productCtaLink = this.getCouponCodeToLink(product.apiData.checkoutLink);
    this.productCtaLinkAriaLabel = product.apiData.title;

    this.calculateSavings();
  }

  private calculateSavings(): void {
    if (!this.discountPrice || this.originalPrice <= 0) return;

    if (this.discountPrice === this.originalPrice) {
      this.percentSaved = ((this.product.apiData.maxPrice - this.discountPrice) / this.product.apiData.maxPrice) * 100;
    } else {
      this.percentSaved = ((this.originalPrice - this.discountPrice) / this.originalPrice) * 100;
    }

    this.percentSavedText = `Save ${this.percentSaved.toFixed(0)}% Today`;
    this.pricingText = this.pricing.getPricingText(this.product, this.data.pricingText);
  }

  /* The content needs time to parse before we can grab the fee selector.
    Instead of wrapping the selector in a timeout, we're activating the wrapping element
    and detecting if the element we just clicked is the '.product-fee span' element.
    If this is true, then we open the modal.*/
  activateModalWrapper(event: MouseEvent, wrapIdenifier?: string): void {
    if (wrapIdenifier && wrapIdenifier === 'wysiwyg') {
      if (
        (event.target as HTMLElement).classList.contains('highlighted-text-modal-1') &&
        this.data.highlightedTextModal1.length > 0
      ) {
        const modalContent = this.data.highlightedTextModal1[0].modalContent;
        this.modal.openModal({ showModal: true, modalContent });
      }
      if (
        (event.target as HTMLElement).classList.contains('highlighted-text-modal-2') &&
        this.data.highlightedTextModal2.length > 0
      ) {
        const modalContent = this.data.highlightedTextModal2[0].modalContent;
        this.modal.openModal({ showModal: true, modalContent });
      }
    }
    if ((event.target as HTMLElement).classList.contains('product-fee-link')) {
      this.modal.openModal({ showModal: true, modalContent: this.feeModalContent });
    }
  }

  /**
   * Retrieves countdown end time from route resolver banner data.
   * Syncs with sticky banner to ensure consistent countdown display.
   * Supports both main promotions and extended reset promotions.
   *
   * @returns Epoch timestamp of promotion end, or null if no active promotion
   */
  private getOfferEndTime = (): number | null => {
    const resolverData = this.route.snapshot.data.pageData as PageDataResponse;

    // Banner data location varies by page structure - check both paths
    const bannerData =
      resolverData?.craftData?.header?.aceHeaderPromoBanner?.[0] || resolverData?.craftData?.banners?.topBanner?.[0];

    if (!bannerData) {
      return null;
    }

    const { promotionStartDateTime, promotionEndsDateTime, promotionReset } = bannerData;

    if (!promotionStartDateTime || !promotionEndsDateTime) {
      return null;
    }

    const now = new Date();
    const startDate = new Date(promotionStartDateTime);
    const endDate = new Date(promotionEndsDateTime);

    // Check main promotion period
    if (now > startDate && now < endDate) {
      return endDate.valueOf();
    }

    // Check reset promotion period (extended sale after main promo ends)
    if (promotionReset?.length > 0) {
      const resetEndDateTime = promotionReset[0].promotionEndsDateTime;
      if (resetEndDateTime) {
        const resetEndDate = new Date(resetEndDateTime);
        if (now >= endDate && now < resetEndDate) {
          return resetEndDate.valueOf();
        }
      }
    }

    return null;
  };

  ctaVideoPlay(): void {
    const wistiaId = this.data.videoButton[0]?.wistiaId;
    if (!wistiaId) {
      logError({ error: new Error('ctaVideoPlay called without a valid wistiaId'), severity: SEVERITY.WARN });
      return;
    }
    this.clientWistia.loadVideoScripts(wistiaId, true);
  }

  /**
   * Builds checkout URL with all available coupons using CheckoutUrlBuilderService
   * This ensures all stackable coupons (email capture + existing ones) are included
   */
  getCouponCodeToLink(link: string): string {
    if (!link) return link;
    // Normalize the host first: a non-checkout handoff (AARP's course-selection) skips the builder below.
    const checkoutLink = this.checkoutUrlBuilderService.toEnvCheckoutHost(link);

    // Use CheckoutUrlBuilderService to handle ALL coupons properly
    if (checkoutLink.toLowerCase().includes('checkout')) {
      return this.checkoutUrlBuilderService.buildCheckoutUrlWithAllCoupons(checkoutLink);
    } else {
      // For non-checkout URLs, apply legacy coupon logic if needed
      const urlCouponPresent = checkoutLink.includes(this.globalCDIQueryParam);
      return `${checkoutLink}${urlCouponPresent ? '' : this.activeCoupon}`;
    }
  }

  /**
   * Validates if webinar course IDs contain at least one valid (non-empty) course ID.
   * @param webinarCourseIds - Array of webinar course objects to validate
   * @returns True if there is at least one valid course ID, false otherwise
   */
  private hasValidCourseIds(webinarCourseIds: WebinarScheduleContent[]): boolean {
    return webinarCourseIds?.some((course) => course.courseid && course.courseid.trim() !== '') || false;
  }

  private injectPreloadLink(src: string): void {
    const link = this.document.createElement('link');
    link.setAttribute('rel', 'preload');
    link.setAttribute('as', 'image');
    link.setAttribute('fetchpriority', 'high');
    link.setAttribute('href', src);
    this.renderer.appendChild(this.document.head, link);
  }

  ngOnDestroy(): void {
    // Invalidates any product fetch still in flight.
    this.productFetchSeq++;
    this.stopPriceRoll();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
